import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import AdminLayout from '../components/AdminLayout';
import { useAdminUser } from '../AdminUserContext';
import { api } from '../../api';

const LANG_LIST = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'id', label: 'Indonesia' },
  { code: 'ms', label: 'Melayu' },
  { code: 'ur', label: 'اردو' },
  { code: 'fa', label: 'فارسی' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'pt', label: 'Português' },
  { code: 'it', label: 'Italiano' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'ha', label: 'Hausa' },
  { code: 'yo', label: 'Yorùbá' },
  { code: 'am', label: 'አማርኛ' },
  { code: 'so', label: 'Soomaali' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'ne', label: 'नेपाली' },
  { code: 'si', label: 'සිංහල' },
  { code: 'ps', label: 'پښتو' },
  { code: 'uz', label: 'Oʻzbekcha' },
  { code: 'tg', label: 'Тоҷикӣ' },
  { code: 'bs', label: 'Bosanski' },
  { code: 'sv', label: 'Svenska' },
  { code: 'lt', label: 'Lietuvių' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'fil', label: 'Filipino' },
  { code: 'ti', label: 'ትግርኛ' },
  { code: 'om', label: 'Oromoo' },
  { code: 'ug', label: 'ئۇيغۇرچە' },
];

function fmtTime(sec) {
  if (!isFinite(sec) || sec < 0) return '0:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AudioControl() {
  const { user } = useAdminUser();
  const isAdmin = user?.role === 'admin' || user?.role === undefined;
  const canControl = isAdmin || user?.role === 'operator';
  const allowedLangs = Array.isArray(user?.allowedLangs) ? user.allowedLangs : [];

  const visibleLangs = (isAdmin || allowedLangs.length === 0)
    ? LANG_LIST
    : LANG_LIST.filter(({ code }) => allowedLangs.includes(code));

  const [tracks, setTracks] = useState({});
  const [durations, setDurations] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [msg, setMsg] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [stoppedLangs, setStoppedLangs] = useState(new Set());
  const [tick, setTick] = useState(0);
  const [langOffsets, setLangOffsets] = useState({});
  const [langSpeeds, setLangSpeeds] = useState({});

  const socketRef = useRef(null);
  const fileInputRefs = useRef({});
  const startedAtRef = useRef(null);
  const isPlayingRef = useRef(false);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const loadTracks = async () => {
    try {
      const data = await api.getAudioTracks();
      const map = {};
      data.forEach((t) => { map[t.code] = t; });
      setTracks(map);
    } catch (err) {
      flash('Error: ' + err.message);
    }
  };

  useEffect(() => { loadTracks(); }, []); // eslint-disable-line

  useEffect(() => {
    LANG_LIST.forEach(({ code }) => {
      if (tracks[code]?.isReady && !durations[code]) {
        const a = new Audio(api.audioUrl(code));
        a.addEventListener('loadedmetadata', () => {
          if (isFinite(a.duration)) setDurations((d) => ({ ...d, [code]: a.duration }));
        });
        a.load();
      }
    });
  }, [tracks]); // eslint-disable-line

  useEffect(() => {
    const id = setInterval(() => {
      if (isPlayingRef.current) setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const socket = io(api.socketUrl(), { transports: ['polling'] });
    socketRef.current = socket;

    socket.on('audio:state', ({ isPlaying: playing, positionMs, langOffsets: lo, stoppedLangs: sl, langSpeeds: ls }) => {
      isPlayingRef.current = playing;
      setIsPlaying(playing);
      setLangOffsets(lo || {});
      setLangSpeeds(ls || {});
      setStoppedLangs(new Set(sl));
      if (playing) startedAtRef.current = Date.now() - positionMs;
    });
    socket.on('audio:play', ({ startedAt, langOffsets: lo }) => {
      startedAtRef.current = startedAt;
      isPlayingRef.current = true;
      setIsPlaying(true);
      setLangOffsets(lo || {});
      setTick(0);
    });
    socket.on('audio:pause', () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
    });
    socket.on('audio:seek_lang', ({ code, offsetMs }) => {
      setLangOffsets((o) => ({ ...o, [code]: offsetMs }));
    });
    socket.on('audio:speed_lang', ({ code, speed }) => {
      setLangSpeeds((s) => ({ ...s, [code]: speed }));
    });
    socket.on('audio:stop_lang', ({ code }) => setStoppedLangs((s) => new Set([...s, code])));
    socket.on('audio:resume_lang', ({ code }) => setStoppedLangs((s) => { const n = new Set(s); n.delete(code); return n; }));

    return () => socket.disconnect();
  }, []);

  const emit = (event, data) => socketRef.current?.emit(event, data);

  const globalElapsed = startedAtRef.current !== null && isPlaying
    ? (Date.now() - startedAtRef.current) / 1000
    : 0;

  const handleUpload = async (code, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [code]: true }));
    setUploadProgress((p) => ({ ...p, [code]: 0 }));
    try {
      await new Promise((resolve, reject) => {
        const form = new FormData();
        form.append('audio', file);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${api.socketUrl()}/api/audio/upload/${code}`);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('admin_token')}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress((p) => ({ ...p, [code]: Math.round((e.loaded / e.total) * 100) }));
        };
        xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(xhr.responseText)));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(form);
      });
      flash(`✓ Uploaded ${code}.mp3`);
      setDurations((d) => { const n = { ...d }; delete n[code]; return n; });
      loadTracks();
    } catch (err) {
      flash('Upload error: ' + err.message);
    } finally {
      setUploading((u) => ({ ...u, [code]: false }));
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete audio for ${code}?`)) return;
    try {
      await api.deleteAudio(code);
      setDurations((d) => { const n = { ...d }; delete n[code]; return n; });
      loadTracks();
    } catch (err) {
      flash('Error: ' + err.message);
    }
  };

  const readyCount = visibleLangs.filter((l) => tracks[l.code]?.isReady).length;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Audio Control</h1>
        <p>
          {isAdmin
            ? 'Upload MP3 translations · Control real-time synchronized playback'
            : `Operator — controlling: ${visibleLangs.map(l => l.label).join(', ')}`}
        </p>
      </div>

      {msg && (
        <div style={{ background: 'rgba(212,170,106,.15)', border: '1px solid var(--a-gold2)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14, color: 'var(--a-gold)' }}>
          {msg}
        </div>
      )}

      {/* ── BROADCAST STATUS PANEL ── */}
      <div style={{ background: 'var(--a-surface)', border: `1px solid ${isPlaying ? 'rgba(34,197,94,.3)' : 'var(--a-border)'}`, borderRadius: 12, padding: 24, marginBottom: 24, transition: 'border-color .3s' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: isPlaying ? 'rgba(34,197,94,.15)' : 'rgba(255,255,255,.04)', border: `2px solid ${isPlaying ? '#22c55e' : 'var(--a-border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: isPlaying ? '#22c55e' : '#444', animation: isPlaying ? 'a-pulse 1.4s ease infinite' : 'none' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: isPlaying ? '#22c55e' : 'var(--a-muted)', textTransform: 'uppercase' }}>
                {isPlaying ? 'Broadcasting' : 'Stopped'}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: isPlaying ? 'var(--a-text)' : 'var(--a-muted)', lineHeight: 1.1, fontFamily: 'monospace' }}>
                {fmtTime(globalElapsed)}
              </div>
            </div>
          </div>

          {/* Admin-only global controls */}
          {isAdmin && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => emit(isPlaying ? 'admin:stop_all' : 'admin:start_all')}
                style={{ padding: '10px 22px', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', background: isPlaying ? '#ef4444' : '#22c55e', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`} />
                {isPlaying ? 'Pause All' : 'Start All'}
              </button>
              <button
                onClick={() => emit('admin:restart_all')}
                style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 13, border: '1px solid var(--a-border)', background: 'var(--a-surface2)', color: 'var(--a-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <i className="fas fa-redo" /> Restart
              </button>
            </div>
          )}

          {/* Operator — show broadcast status only */}
          {!isAdmin && !isPlaying && (
            <div style={{ fontSize: 13, color: 'var(--a-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-info-circle" />
              Waiting for admin to start the broadcast
            </div>
          )}
        </div>

        <div style={{ background: 'var(--a-surface2)', borderRadius: 6, height: 6, overflow: 'hidden' }}>
          <div style={{ width: isPlaying ? '100%' : '0%', height: '100%', background: 'linear-gradient(90deg, #22c55e, var(--a-gold))', animation: isPlaying ? 'a-progress-anim 60s linear infinite' : 'none', borderRadius: 6 }} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--a-muted)', marginTop: 6 }}>
          {readyCount}/{visibleLangs.length} languages ready · Arabic audio comes from the live stream
        </div>
      </div>

      {/* ── PER-LANGUAGE TABLE ── */}
      <div className="a-table-wrap">
        <div className="a-table-header">
          <h3>Languages</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="a-badge green">{readyCount} ready</span>
            <span className="a-badge">{visibleLangs.length - readyCount} missing</span>
          </div>
        </div>
        <table className="a-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Code</th>
              <th>Language</th>
              <th style={{ width: 200 }}>Progress</th>
              {isAdmin && <th style={{ width: 220 }}>Upload</th>}
              <th>Controls</th>
            </tr>
          </thead>
          <tbody>
            {visibleLangs.map((l) => {
              const track = tracks[l.code];
              const isReady = track?.isReady;
              const isStopped = stoppedLangs.has(l.code);
              const isUp = uploading[l.code];
              const progress = uploadProgress[l.code] || 0;
              const dur = durations[l.code];
              const offsetMs = langOffsets[l.code] || 0;
              const speed = langSpeeds[l.code] || 1;
              const langElapsed = isPlaying && startedAtRef.current
                ? Math.max(0, (Date.now() - startedAtRef.current + offsetMs) / 1000) * speed
                : 0;
              const pct = dur ? Math.min(100, (langElapsed / dur) * 100) : 0;
              const remaining = dur ? Math.max(0, dur - langElapsed) : null;

              return (
                <tr key={l.code} style={{ opacity: isStopped ? 0.55 : 1 }}>
                  <td><span className="a-badge blue">{l.code}</span></td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{l.label}</span>
                    {isStopped && <span className="a-badge red" style={{ marginLeft: 6, fontSize: 11 }}>stopped</span>}
                  </td>

                  {/* Progress */}
                  <td>
                    {isReady && isPlaying && dur ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--a-muted)', marginBottom: 4 }}>
                          <span style={{ fontFamily: 'monospace' }}>{fmtTime(langElapsed)}</span>
                          <span style={{ fontFamily: 'monospace' }}>-{fmtTime(remaining)}</span>
                        </div>
                        <div style={{ background: 'var(--a-surface2)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: isStopped ? '#ef4444' : 'var(--a-gold)', borderRadius: 4, transition: 'width 1s linear' }} />
                        </div>
                      </div>
                    ) : isReady ? (
                      <span style={{ color: 'var(--a-muted)', fontSize: 12 }}>{dur ? fmtTime(dur) + ' total' : 'Loading…'}</span>
                    ) : (
                      <span style={{ color: 'var(--a-muted)', fontSize: 12 }}>—</span>
                    )}
                  </td>

                  {/* Upload — admin only */}
                  {isAdmin && (
                    <td>
                      {isUp ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--a-surface2)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${progress}%`, height: '100%', background: 'var(--a-gold)', borderRadius: 3, transition: 'width .2s' }} />
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--a-muted)', minWidth: 32 }}>{progress}%</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="file"
                            accept=".mp3,audio/mpeg"
                            style={{ display: 'none' }}
                            ref={(el) => { fileInputRefs.current[l.code] = el; }}
                            onChange={(e) => handleUpload(l.code, e.target.files[0])}
                          />
                          <button
                            className="a-icon-btn"
                            onClick={() => fileInputRefs.current[l.code]?.click()}
                            style={{ fontSize: 12 }}
                          >
                            <i className="fas fa-upload" /> {isReady ? 'Replace' : 'Upload'}
                          </button>
                          {isReady && (
                            <button className="a-icon-btn danger" onClick={() => handleDelete(l.code)} style={{ fontSize: 12 }}>
                              <i className="fas fa-trash" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )}

                  {/* Per-track controls — admin and operator */}
                  <td>
                    {isReady && canControl ? (
                      <div className="a-btn-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {/* Pause / Resume */}
                        {isStopped ? (
                          <button
                            className="a-icon-btn success"
                            onClick={() => emit('admin:resume_lang', { code: l.code })}
                            style={{ fontSize: 12 }}
                          >
                            <i className="fas fa-play" /> Resume
                          </button>
                        ) : (
                          <button
                            className="a-icon-btn danger"
                            onClick={() => emit('admin:stop_lang', { code: l.code })}
                            disabled={!isPlaying}
                            style={{ fontSize: 12 }}
                          >
                            <i className="fas fa-pause" /> Pause
                          </button>
                        )}

                        {/* Speed: two separate buttons */}
                        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                          <button
                            className="a-icon-btn"
                            onClick={() => {
                              emit('admin:speed_lang', { code: l.code, speed: 1 });
                              if (speed === 2) emit('admin:seek_lang', { code: l.code, deltaMs: -30000 });
                            }}
                            disabled={!isPlaying}
                            title="Normal speed"
                            style={{
                              fontSize: 12, fontWeight: 700, minWidth: 34, padding: '4px 8px',
                              ...(speed === 1 ? { background: 'rgba(34,197,94,.15)', borderColor: '#22c55e', color: '#22c55e' } : {}),
                            }}
                          >
                            ×1
                          </button>
                          <button
                            className="a-icon-btn"
                            onClick={() => {
                              emit('admin:speed_lang', { code: l.code, speed: 2 });
                              if (speed === 1) emit('admin:seek_lang', { code: l.code, deltaMs: 30000 });
                            }}
                            disabled={!isPlaying}
                            title="Double speed — also advances track by 30s"
                            style={{
                              fontSize: 12, fontWeight: 700, minWidth: 34, padding: '4px 8px',
                              ...(speed === 2 ? { background: 'rgba(234,179,8,.15)', borderColor: '#eab308', color: '#eab308' } : {}),
                            }}
                          >
                            ×2
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--a-muted)', fontSize: 12 }}>
                        {isAdmin ? 'Upload first' : 'Audio not uploaded yet'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes a-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: .7; }
        }
        @keyframes a-progress-anim {
          0% { margin-left: -100%; width: 60%; }
          100% { margin-left: 100%; width: 60%; }
        }
      `}</style>
    </AdminLayout>
  );
}
